import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Pipe, EventEmitter, computed, Component, ChangeDetectionStrategy, Input, Output } from '@angular/core';
import { Duration, DateTime } from 'luxon';
import { timer, map, takeWhile, tap } from 'rxjs';

class CountDownFormatPipe {
    transform(milliseconds) {
        if (!milliseconds) {
            return '';
        }
        const date = Duration.fromMillis(milliseconds).shiftTo('days', 'hours', 'minutes', 'seconds').toObject();
        const days = date.days;
        const hours = date.hours;
        const minutes = date.minutes;
        const seconds = date.seconds;
        if (days) {
            return `${days}D ${hours}H`;
        }
        else if (hours) {
            return `${hours}H ${minutes}M`;
        }
        else if (minutes) {
            return `${minutes}M ${seconds}S`;
        }
        else {
            return `${seconds}S`;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: CountDownFormatPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.2.1", ngImport: i0, type: CountDownFormatPipe, isStandalone: true, name: "countdownFormat" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: CountDownFormatPipe, decorators: [{
            type: Pipe,
            args: [{
                    standalone: true,
                    name: 'countdownFormat'
                }]
        }] });

/**
 * Countdown version 2 of the countdown component, this component uses luxon for date time manipulation.
 * This is more accurate than the previous countdown component as this one sets the millisecond to 0 while
 * the old component uses math.floor to get the remaining time and completes sometimes ahead of 1 second.
 * When the old component triggers complete when comparing the start date and now date again, the remaining time is not accurate as sometimes the result
 * will be behind by 1 second resulting to false
 * @example <fgbcl-countdown-v2 [endDateTime]="comingSoonDate" (complete)="itemStart.emit()"></fgbcl-countdown-v2>
 *
 */
class CountdownV2Component {
    constructor() {
        /** The text to display in the countdown upon expiry. */
        this.completeText = 'Complete';
        /** determines whether component will receive local or utc time. default is utc. */
        this.isUTC = true;
        /** Emitted upon countdown expiry. */
        this.complete = new EventEmitter();
        this.endDate = computed(() => this.isUTC ? DateTime.fromISO(this.endDateTime, { zone: 'utc' }) : DateTime.fromISO(this.endDateTime));
        this.completed = false;
    }
    ngOnInit() {
        this.now = this.isUTC ? DateTime.utc() : DateTime.now();
        if (this.startDateTime) {
            this.now = this.isUTC ? DateTime.fromISO(this.startDateTime, { zone: 'utc' }) : DateTime.fromISO(this.startDateTime);
        }
        this.startTimer();
    }
    startTimer() {
        const remainingTime = this.endDate().diff(this.now.set({ millisecond: 0 })).toObject();
        const remainingTimeInMillis = Duration.fromObject(remainingTime).as('milliseconds');
        this.remainingTime$ = timer(0, 1000).pipe(map((x) => remainingTimeInMillis - (x * 1000)), 
        // Stop the timer when the countdown is completed
        takeWhile(() => !this.completed), tap(milliseconds => {
            // // if milliseconds is less than or equal to 0, then the countdown is completed
            if (milliseconds <= 0) {
                this.completed = true;
                // Notify the parent component that the countdown is completed
                this.complete.emit(this.completed);
            }
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: CountdownV2Component, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.2.1", type: CountdownV2Component, isStandalone: true, selector: "fgbcl-countdown-v2", inputs: { startDateTime: "startDateTime", endDateTime: "endDateTime", completeText: "completeText", isUTC: "isUTC" }, outputs: { complete: "complete" }, ngImport: i0, template: "<span *ngIf=\"!completed; else showComplete\" class=\"countdown-timer fw-bold\">\r\n  {{ remainingTime$ | async | countdownFormat }}\r\n</span>\r\n<ng-template #showComplete>\r\n  <span>\r\n    {{ completeText }}\r\n  </span>\r\n</ng-template>\r\n", styles: [""], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: CountDownFormatPipe, name: "countdownFormat" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: CountdownV2Component, decorators: [{
            type: Component,
            args: [{ selector: 'fgbcl-countdown-v2', standalone: true, imports: [CommonModule, CountDownFormatPipe], changeDetection: ChangeDetectionStrategy.OnPush, template: "<span *ngIf=\"!completed; else showComplete\" class=\"countdown-timer fw-bold\">\r\n  {{ remainingTime$ | async | countdownFormat }}\r\n</span>\r\n<ng-template #showComplete>\r\n  <span>\r\n    {{ completeText }}\r\n  </span>\r\n</ng-template>\r\n" }]
        }], propDecorators: { startDateTime: [{
                type: Input
            }], endDateTime: [{
                type: Input,
                args: [{ required: true }]
            }], completeText: [{
                type: Input
            }], isUTC: [{
                type: Input
            }], complete: [{
                type: Output
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { CountdownV2Component };
